export const isImageLightOrDarkFromURLWithAlphaChannel = (imageURL): Promise<boolean> => {
	return new Promise((resolve, reject) => {
		const img = new Image();
		img.crossOrigin = 'anonymous'; // CORS 이슈 방지
		img.src = imageURL;

		img.onload = () => {
			const canvas = document.createElement('canvas');
			const ctx = canvas.getContext('2d')!;
			canvas.width = img.width;
			canvas.height = img.height;

			// 이미지를 캔버스에 그리기
			ctx!.drawImage(img, 0, 0, canvas.width, canvas.height);

			// 픽셀 데이터 가져오기
			const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
			const data = imageData.data;

			let totalBrightness = 0;
			let validPixelCount = 0;

			for (let i = 0; i < data.length; i += 4) {
				const r = data[i]; // Red
				const g = data[i + 1]; // Green
				const b = data[i + 2]; // Blue
				const a = data[i + 3]; // Alpha (투명도, 0~255)

				if (a > 0) {
					// 투명하지 않은 픽셀만 고려
					const brightness = 0.299 * r + 0.587 * g + 0.114 * b;
					totalBrightness += brightness;
					validPixelCount++;
				}
			}

			if (validPixelCount === 0) {
				// 모든 픽셀이 투명하면 기본적으로 '밝음'으로 간주
				resolve(true);
				return;
			}

			// 평균 밝기 계산
			const averageBrightness = totalBrightness / validPixelCount;

			// 흰색(밝음)인지 검은색(어두움)인지 판단
			resolve(averageBrightness > 128);
		};

		img.onerror = () => {
			reject('Failed to load image from URL');
		};
	});
};

export const isImageLightOrDarkFromURLWithoutAlphaChannel = (imageURL): Promise<boolean> => {
	return new Promise((resolve, reject) => {
		const img = new Image();
		img.crossOrigin = 'anonymous'; // CORS 문제 방지
		img.src = imageURL;

		img.onload = () => {
			const canvas = document.createElement('canvas');
			const ctx = canvas.getContext('2d')!;
			canvas.width = img.width;
			canvas.height = img.height;

			// 이미지를 캔버스에 그리기
			ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

			// 픽셀 데이터 가져오기
			const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
			const data = imageData.data;

			let totalBrightness = 0;

			for (let i = 0; i < data.length; i += 4) {
				const r = data[i]; // Red
				const g = data[i + 1]; // Green
				const b = data[i + 2]; // Blue

				// 밝기 계산
				const brightness = 0.299 * r + 0.587 * g + 0.114 * b;
				totalBrightness += brightness;
			}

			const averageBrightness = totalBrightness / (data.length / 4);

			resolve(averageBrightness > 128);
		};

		img.onerror = () => {
			reject('Failed to load image from URL');
		};
	});
};

export const isImageWithAlphaChannel = (src: string): boolean => {
	const ext = src.split('.').pop();
	if (!ext) {
		throw new Error('Failed to get extension from image URL');
	}
	return ['png', 'webp'].includes(ext.toLowerCase());
};

export const isImageBright = async (imageURL: string): Promise<boolean> => {
	const isImageWithAlpha = isImageWithAlphaChannel(encodeURI(imageURL));
	if (isImageWithAlpha) {
		return isImageLightOrDarkFromURLWithAlphaChannel(encodeURI(imageURL));
	} else {
		return isImageLightOrDarkFromURLWithoutAlphaChannel(encodeURI(imageURL));
	}
};

export const adjustBrightness = (color: string): string => {
	// #을 제거하고 RGB 값으로 변환
	let r = parseInt(color.slice(1, 3), 16);
	let g = parseInt(color.slice(3, 5), 16);
	let b = parseInt(color.slice(5, 7), 16);

	// 밝기 계산 (표준 공식: 0.2126 * R + 0.7152 * G + 0.0722 * B)
	const brightness = 0.2126 * r + 0.7152 * g + 0.0722 * b;

	// 밝기 단계별 amount 값 설정 (0.05 = 5%)
	let amount = 0;
	if (brightness > 200) {
		amount = 0.5; // 가장 밝은 단계
	} else if (brightness > 160) {
		amount = 0.4;
	} else if (brightness > 128) {
		amount = 0.3;
	} else if (brightness > 100) {
		amount = 0.2; // 어두운 단계는 덜 조정
	} else if (brightness > 50) {
		amount = 0.1; // 매우 어두운 경우 거의 변경하지 않음
	}

	// RGB 값에 amount를 적용하여 어둡게
	r = Math.max(0, Math.floor(r * (1 - amount)));
	g = Math.max(0, Math.floor(g * (1 - amount)));
	b = Math.max(0, Math.floor(b * (1 - amount)));

	// 16진수 문자열로 변환
	return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
};

export const replaceImageSrcForAgentIcon = (src: string) => {
	return src.replace(
		'https://cdn.winging.app/',
		'https://cdn.winging.app/cdn-cgi/image/width=60,height=60,quality=80,fit=cover/'
	);
};
export const replaceImageSrcForLogo = (src: string) => {
	return src.replace(
		'https://cdn.winging.app/',
		'https://cdn.winging.app/cdn-cgi/image/height=60,quality=80/'
	);
};

export const replaceImageSizeSrc = (src: string, width: number, quality?: number) => {
	return src.replace(
		'https://cdn.winging.app/',
		`https://cdn.winging.app/cdn-cgi/image/width=${width},quality=${quality || 80},fit=scale-down/`
	);
};

export const replaceImageSrcWithQuery = (src: string, query: string) => {
	return src.replace('https://cdn.winging.app/', `https://cdn.winging.app/${query}/`);
};
